import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from '../services/auth.service';

/** Rol que solo accede a Torre de Control y a los módulos de Tarifas asignados. */
export const TRANSPORTISTA_ROLE = 'transportista';

/** Ruta de la Torre de Control. */
export const TORRE_CONTROL_ROUTE = '/torre-control-transporte';

/** Código del módulo Torre de Control (`modules.code`). */
export const TORRE_CONTROL_MODULE_CODE = 'torreControl';

/** Pantalla para el transportista que no tiene ningún módulo asignado. */
export const SIN_MODULOS_ROUTE = '/sin-modulos';

/**
 * Pantalla de inicio del transportista según sus módulos asignados.
 * La Torre de Control no se le agrega sola: si no la tiene asignada, entra a
 * su primer módulo de Tarifas, y si no tiene ninguno, a la pantalla que lo
 * explica. Nunca apunta a una ruta que el guard le vaya a negar (sin bucles).
 */
export function transportistaLanding(modules: string[] | undefined): string {
    const assigned = modules ?? [];
    if (assigned.includes(TORRE_CONTROL_MODULE_CODE)) {
        return TORRE_CONTROL_ROUTE;
    }
    // Para el transportista, todo módulo que no sea la Torre es un módulo
    // embebido de Tarifas: el backend no le entrega otros.
    const firstIntegration = [...assigned].sort()[0];
    return firstIntegration ? `/integraciones/${firstIntegration}` : SIN_MODULOS_ROUTE;
}

/** Pantalla de inicio del usuario actual: la del transportista, o /home para los demás roles. */
export function homeRouteFor(authService: AuthService): string {
    return authService.hasRole(TRANSPORTISTA_ROLE)
        ? transportistaLanding(authService.getCurrentUser()?.modules)
        : '/home';
}

/**
 * ¿Puede el transportista abrir esta URL? (feat-rol-transportista, D10)
 * Permitidas: la Torre de Control (además exige el módulo asignado, ver
 * moduleGuard), los módulos embebidos (`/integraciones/…`, el backend decide
 * cuáles con 403), la pantalla "sin módulos" y la autenticación.
 * El backend aplica la misma regla: esto solo evita pantallas que fallarían.
 */
export function isUrlAllowedForTransportista(url: string): boolean {
    const path = url.split(/[?#]/)[0];
    return (
        path === TORRE_CONTROL_ROUTE ||
        path.startsWith(`${TORRE_CONTROL_ROUTE}/`) ||
        path.startsWith('/integraciones/') ||
        path === SIN_MODULOS_ROUTE ||
        path === '/authentication' ||
        path.startsWith('/authentication/')
    );
}

/** UrlTree a su pantalla de inicio si el usuario es transportista y la URL no está permitida; si no, null. */
export function transportistaRedirect(
    authService: AuthService,
    router: Router,
    url: string
): UrlTree | null {
    if (!authService.hasRole(TRANSPORTISTA_ROLE) || isUrlAllowedForTransportista(url)) {
        return null;
    }
    return router.parseUrl(homeRouteFor(authService));
}

/**
 * Para rutas sin `authGuard` (la ruta `**`): un transportista autenticado que
 * escribe una URL inexistente vuelve a su inicio; los demás ven el 404 de siempre.
 */
export const transportistaAllowlistGuard: CanActivateFn = (_route, state) => {
    const authService = inject(AuthService);
    if (!authService.isAuthenticated()) {
        return true;
    }
    return transportistaRedirect(authService, inject(Router), state.url) ?? true;
};
